import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import download from 'downloadjs';
import { useUnit } from 'effector-react';
import moment from 'moment';
import { Route, Routes as RoutesComponent, useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AccountActionDialog } from './AccountActionDialog';
import { CLIENT_STATUS_AF, CLIENT_STATUS_AC, CLIENT_STATUS_IB, CLIENT_STATUS_NONE, AttachDetach } from './AttachDetach';
import { CalcRebalanceDialog } from './CalcRebalanceDialog';
import { ClientComments } from './ClientComments';
import { ClientIirExportDialog } from './ClientIirExportDialog';
import { ProfitChart } from './ProfitChart';
import { ProfitTable } from './ProfitTable';
import { useClientReport, useClientProblem } from './queries';
import { SignalHistory } from './SignalHistory';
import { SignalTable } from './SignalTable';
import { rebalanceClient, calcRebalanceClient, blockClient, getClientAccepts } from '../../../api';
import { ChartLegend } from '../../../components/Chart/ChartLegend';
import { FREE_MONEY, PieChart } from '../../../components/Chart/PieChart';
import { FilterSection } from '../../../components/FilterSection';
import { Navigation } from '../../../components/Navigation';
import { ProtectedComponent } from '../../../components/ProtectedComponent';
import { ClientDetails$, clientDetailsApi } from '../../../effector/clientDetails';
import { ClientInfo, StatusEnum } from '../../../types/clientDetails';
import { PageParams, SortType } from '../../../types/pagination';
import { UserAction } from '../../../utils/permissions';
import { Nullable } from '../../../utils/types';
import {
  PAGE_STATISTIC,
  PAGE_STATISTIC_CLIENT_DETAILS_INFO,
  PAGE_STATISTIC_CLIENT_DETAILS_PROFIT_CHART,
  PAGE_STATISTIC_CLIENT_DETAILS_PROFIT_TABLE,
  PAGE_STATISTIC_CLIENT_DETAILS_SIGNALS,
  PAGE_STATISTIC_CLIENT_INFO,
} from '../../Routes';

type IntervalOption = {
  value: string;
  label: string;
};

const DELAY = 500;

const blockOptions: IntervalOption[] = [
  { value: '01:00:00', label: '1 час' },
  { value: '02:00:00', label: '2 часа' },
  { value: '04:00:00', label: '4 часа' },
  { value: '12:00:00', label: '12 часов' },
  { value: '24:00:00', label: '1 день' },
  { value: '72:00:00', label: '3 дня' },
  { value: '1000000:00:00', label: 'навсегда' },
];

export const ClientDetails: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [showIirExportDialog, setShowIirExportDialog] = useState(false);
  const [ticker, setTicker] = useState('');
  const params = useParams();
  const routeAgreementId = params['*']?.split('/')?.[1] ?? '';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { positions, clientInfo } = useUnit(ClientDetails$);
  const isUnsubscribeClient = searchParams.get('source') === 'unsubscribe';

  const { mutateAsync, isPending: isReportLoading } = useClientReport(clientInfo?.agreementId, isUnsubscribeClient);
  const { data: clientProblemData } = useClientProblem(clientInfo?.agreement);

  const clientProblem = clientProblemData?.result?.pageStats?.[0]?.description ?? null;

  const timerId = React.useRef<NodeJS.Timer>(undefined);

  useEffect(() => {
    if (/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(routeAgreementId)) {
      clientDetailsApi.getClientInfoById(routeAgreementId);
    }
    return () => {
      clientDetailsApi.reset('');
    };
  }, [routeAgreementId]);

  useEffect(() => {
    if (clientInfo?.agreementId) {
      clientDetailsApi.getPositions(clientInfo.agreementId);

      const intervalId = window.setInterval(() => {
        clientDetailsApi.getPositions(clientInfo.agreementId);
      }, 10000);

      return () => {
        window.clearInterval(intervalId);
      };
    }
  }, [clientInfo]);

  const handleTickerChange = useCallback(
    (value: string) => {
      setTicker(value);

      if (value.length <= 3) return;

      if (timerId.current) {
        clearTimeout(timerId.current as unknown as number);
      }

      timerId.current = setTimeout(() => {
        clientDetailsApi
          .getClientInfo(value)
          .then((result) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            if (result.success) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              const newData: ClientInfo = result.result?.[0] || result.result;
              setErrorMessage('');

              if (newData) {
                navigate(`${PAGE_STATISTIC_CLIENT_DETAILS_INFO}/${newData.agreementId}`, { replace: true });
              }
            } else {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              setErrorMessage(result.errorMessage);
            }
          })
          .catch(() => {
            setErrorMessage('Произошла непредвиденная ошибка');
          });
      }, DELAY);
    },
    [navigate],
  );

  const handleReset = useCallback(() => {
    setTicker('');
    setErrorMessage('');
  }, []);

  const piePositions = useMemo(() => {
    return positions.map(({ securityKey, name, weight, number, currency, avgPrice, lastPrice }) => ({
      symbol: securityKey,
      name: securityKey && securityKey.length > 0 ? name : FREE_MONEY,
      number,
      currency,
      weight,
      avgPrice,
      lastPrice,
    }));
  }, [positions]);

  const clientStatus = useMemo(() => {
    if (!clientInfo) return '';

    if (clientInfo.status === StatusEnum.AutoFollow) return CLIENT_STATUS_AF;
    if (clientInfo.status === StatusEnum.Recommendations) return CLIENT_STATUS_AC;
    if (clientInfo.status === StatusEnum.InvestMoneybox) return CLIENT_STATUS_IB;

    return CLIENT_STATUS_NONE;
  }, [clientInfo]);

  const loadHistory = useCallback(
    (data: PageParams) => {
      if (data.sortFieldName === 'datetime' && clientInfo) {
        const params = {
          agreementId: clientInfo?.agreementId,
          sortType: data.sortDirection === SortType.Desk ? 'd' : '',
          startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
        };
        clientDetailsApi.getHistory(params);
      }
    },
    [clientInfo],
  );

  const handleGetClientAccepts = () => getClientAccepts(clientInfo?.agreementId);

  const handleGetReport = async () => {
    setErrorMessage('');
    try {
      const result = await mutateAsync();

      if (!result.success) {
        setErrorMessage(result.errorMessage || 'Произошла непредвиденная ошибка');
        return;
      }

      download(
        `data:application/octet-stream;base64,${result.result.contentBase64}`,
        `${result.result.fileName}`,
        'application/octet-stream',
      );
    } catch {
      setErrorMessage('Произошла непредвиденная ошибка');
    }
  };

  const infoRows = useMemo(() => {
    if (!clientInfo?.agreementId) return [];

    const rows: { label: string; value: React.ReactNode; protected?: boolean; condition?: boolean }[] = [
      { label: 'Клиент', value: clientInfo.fio, protected: true },
      { label: 'Ген. соглашение', value: clientInfo.agreement },
      { label: 'Клиент код', value: clientInfo.clientCode },
      { label: 'УНК клиента', value: clientInfo.clientId },
      {
        label: 'Уровень привилегий',
        value: clientInfo.servicePackageName,
        condition: !!clientInfo.servicePackageName,
      },
      { label: 'Статус', value: clientStatus },
      { label: 'Стратегия', value: clientInfo.strategyName },
      { label: 'Маржинальное кредитование', value: clientInfo.isMarginal ? 'Да' : 'Нет' },
      {
        label: 'Непройденные проверки',
        value: clientInfo.checksNeeded?.length === 0 ? 'Все необходимые пройдены' : clientInfo.checksNeeded?.join(', '),
        condition: !!clientInfo.checksNeeded,
      },
      { label: 'Блокировка', value: clientInfo.block === null ? '-' : clientInfo.block },
      { label: 'Проблема', value: clientProblem ?? '-' },
      {
        label: 'Заблокированные позиции',
        value:
          Object.keys(clientInfo.blockedPositions || {}).length === 0
            ? '-'
            : Object.entries(clientInfo.blockedPositions!)
                .map(([key, value]) => `${key} = ${value}`)
                .join(', '),
        condition: !!clientInfo?.blockedPositions,
      },
      {
        label: 'Блокирующий сигнал',
        value: clientInfo.blockSignalId === null ? '-' : clientInfo.blockSignalId,
      },
    ];

    return rows.filter((row) => row.condition === undefined || row.condition);
  }, [clientInfo, clientStatus, clientProblem]);

  return (
    <>
      <FilterSection>
        <Stack direction="row" gap={2} alignItems="center">
          <TextField
            label="Г/C или Клиент код"
            variant="outlined"
            fullWidth
            margin="normal"
            value={ticker}
            onChange={(e) => handleTickerChange(e.target.value)}
            aria-label="Поиск клиента по Г/С или Клиент коду"
          />
          <Button variant="outlined" color="primary" size="large" sx={{ mt: 1, minWidth: 140 }} onClick={handleReset}>
            Сбросить
          </Button>
        </Stack>
      </FilterSection>

      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {clientInfo?.agreementId && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mt: 2,
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              flex: clientStatus === CLIENT_STATUS_NONE ? 1 : '1 1 0',
              minWidth: 0,
              display: 'flex',
              alignSelf: 'stretch',
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 2,
                width: '100%',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Информация о клиенте
              </Typography>

              <Box sx={{ flex: 1, minHeight: 0 }}>
                {infoRows.map((row) => {
                  const content = (
                    <Stack key={row.label} direction="row" spacing={1} sx={{ py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 220, flexShrink: 0 }}>
                        {row.label}:
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {row.value}
                      </Typography>
                    </Stack>
                  );

                  if (row.protected) {
                    return (
                      <ProtectedComponent key={row.label} userAction={UserAction.statisticClientDetailsPersonal}>
                        {content}
                      </ProtectedComponent>
                    );
                  }

                  return content;
                })}
              </Box>

              <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={handleGetClientAccepts}>
                  Выгрузить акцепты
                </Button>
                <Button variant="contained" onClick={() => setShowIirExportDialog(true)}>
                  Выгрузить ИИР
                </Button>
                <Button variant="contained" onClick={handleGetReport} disabled={isReportLoading}>
                  {isReportLoading ? 'Формирование отчёта…' : 'Выгрузить отчет'}
                </Button>
              </Stack>
            </Paper>
          </Box>

          {(clientStatus === CLIENT_STATUS_AC ||
            clientStatus === CLIENT_STATUS_AF ||
            clientStatus === CLIENT_STATUS_IB) && (
            <AccountManager
              id={clientInfo.agreementId}
              agreement={clientInfo.agreement}
              strategyId={clientInfo.strategyId}
              type={clientInfo.status}
            />
          )}
        </Box>
      )}

      {clientInfo?.agreementId && <ClientComments key={clientInfo.agreementId} agreementId={clientInfo.agreementId} />}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: 2,
          mt: 2,
          alignItems: 'flex-start',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ flex: '1 1 0', minWidth: 0, maxWidth: '100%' }}>
          {clientInfo?.agreementId && (
            <ClientDetailsRoute
              agreementId={clientInfo.agreementId}
              options={loadHistory}
              isUnsubscribeClient={isUnsubscribeClient}
            />
          )}
        </Box>
        <Box
          sx={{
            width: { xs: '100%', lg: 320 },
            flexShrink: 0,
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          {piePositions && piePositions.length > 0 && (
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <PieChart data={piePositions} />
              <ChartLegend positions={piePositions} />
            </Paper>
          )}
          {clientInfo && clientInfo.agreementId && (
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="body2">Ликвид. стоимость: {clientInfo?.portfolio?.liquid_price}</Typography>
              <Typography variant="body2">Исходная стоимость: {clientInfo?.portfolio?.portfolio_price}</Typography>
              <Typography variant="body2">Realized PnL: {clientInfo?.realizedPnl}</Typography>
              <ProtectedComponent userAction={UserAction.statisticClientInfo}>
                <Link to={`${PAGE_STATISTIC}/${PAGE_STATISTIC_CLIENT_INFO}/${clientInfo.agreementId}`}>
                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                    Детальная информация о клиенте
                  </Typography>
                </Link>
              </ProtectedComponent>
            </Paper>
          )}
        </Box>
      </Box>

      {showIirExportDialog && clientInfo?.agreementId && (
        <ClientIirExportDialog agreementId={clientInfo.agreementId} onClose={() => setShowIirExportDialog(false)} />
      )}
    </>
  );
};

const ClientDetailsRoute: React.FC<{
  agreementId: string;
  options: (arg: PageParams) => void;
  isUnsubscribeClient: boolean;
}> = ({ agreementId, options, isUnsubscribeClient }) => {
  const clientDetailsPath = (path: string) => `${path}${isUnsubscribeClient ? '?source=unsubscribe' : ''}`;

  const items = [
    {
      name: 'История сделок',
      path: clientDetailsPath(`${PAGE_STATISTIC_CLIENT_DETAILS_INFO}/${agreementId}`),
    },
    {
      name: 'График доходности',
      path: clientDetailsPath(`${PAGE_STATISTIC_CLIENT_DETAILS_PROFIT_CHART}/${agreementId}`),
    },
    {
      name: 'Таблица доходности',
      path: clientDetailsPath(`${PAGE_STATISTIC_CLIENT_DETAILS_PROFIT_TABLE}/${agreementId}`),
    },
    {
      name: 'История сигналов',
      path: clientDetailsPath(`${PAGE_STATISTIC_CLIENT_DETAILS_SIGNALS}/${agreementId}`),
    },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Navigation items={items} />
      <Box sx={{ mt: 2, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
        <RoutesComponent>
          <Route path="profit_chart/:clientId" element={<ProfitChart agreementId={agreementId} />} />
          <Route path="profit_table/:clientId" element={<ProfitTable agreementId={agreementId} />} />
          <Route path="signals/:clientId" element={<SignalTable agreementId={agreementId} />} />
          <Route path="info?/:clientId?" element={<SignalHistory agreementId={agreementId} options={options} />} />
        </RoutesComponent>
      </Box>
    </Box>
  );
};

type ActionDialogState = {
  open: boolean;
  title: string;
  description: string;
  action: (() => Promise<unknown>) | null;
};

const AccountManager: React.FC<{ id: string; agreement: string; strategyId: Nullable<string>; type: StatusEnum }> = ({
  id,
  agreement,
  strategyId,
  type,
}) => {
  const [blockedPeriod, setBlockedPeriod] = useState('');
  const [actionDialog, setActionDialog] = useState<ActionDialogState>({
    open: false,
    title: '',
    description: '',
    action: null,
  });
  const [showCalcDialog, setShowCalcDialog] = useState(false);

  const handleCloseAction = () => {
    setActionDialog({ open: false, title: '', description: '', action: null });
  };

  const handleRebalance = () => {
    setActionDialog({
      open: true,
      title: 'Управление аккаунтом',
      description: `Выполнить балансировку аккаунта для ГС ${agreement ?? 'не определено'}`,
      action: () => rebalanceClient({ agreementId: id }),
    });
  };

  const handleBlock = () => {
    const selectedOption = blockOptions.find((opt) => opt.value === blockedPeriod);
    setActionDialog({
      open: true,
      title: 'Управление аккаунтом',
      description: `Заблокировать аккаунт ГС ${agreement ?? 'не определено'} на ${selectedOption?.label ?? ''}`,
      action: () => blockClient({ agreementId: id, time: blockedPeriod || '00:00:00' }),
    });
  };

  const handlePeriodChange = (event: SelectChangeEvent<string>) => {
    setBlockedPeriod(event.target.value);
  };

  return (
    <Box
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        alignSelf: 'stretch',
      }}
    >
      <Paper
        elevation={1}
        sx={{
          p: 2,
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Управление
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={handleRebalance}>
            Балансировать
          </Button>
        </Stack>

        <ProtectedComponent userAction={UserAction.statisticClientInfo}>
          <AttachDetach id={id} strategyId={strategyId} type={type} />
        </ProtectedComponent>

        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
          Блокировка данного аккаунта
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="block-period-label">Интервал</InputLabel>
            <Select
              labelId="block-period-label"
              label="Интервал"
              value={blockedPeriod}
              onChange={handlePeriodChange}
              aria-label="Выбор интервала блокировки"
            >
              {blockOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" disabled={!blockedPeriod} onClick={handleBlock}>
            Заблокировать
          </Button>
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={() => setShowCalcDialog(true)}>
            Рассчитать балансировку
          </Button>
        </Box>

        {actionDialog.open && actionDialog.action && (
          <AccountActionDialog
            title={actionDialog.title}
            description={actionDialog.description}
            action={actionDialog.action}
            onClose={handleCloseAction}
          />
        )}

        {showCalcDialog && (
          <CalcRebalanceDialog action={() => calcRebalanceClient(id)} onClose={() => setShowCalcDialog(false)} />
        )}
      </Paper>
    </Box>
  );
};
